import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import AudioFinishedButtons from './components/FinishedButtons/AudioFinishedButtons';
import FlashFinishedButtons from './components/FinishedButtons/FlashFinishedButtons';
import HomeworkFinishedButtons from './components/FinishedButtons/HomeworkFinishedButtons';
import Navigate from './components/Navigate/Navigate';
import { Expand, Speaker } from './components/RoundButtons/RoundButtons';
import './NavigationBox.module.scss';

export type Displaymode = 'absolute' | 'centered-y' | 'centered' | 'default';

type Props = {
    displayMode: Displaymode;
    top?: string;
    right?: string;
    left?: string;
    x?: string;
    y?: string;
    title?: string;
    text?: string | React.ReactNode;
    subtitle?: string;
    exercise?: string;
    next?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    previous?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    audio?: string;
    finishedButtons?: 'homework' | 'audio-exercise' | 'flash-exercise' | false;
    ProgressBar?: React.ReactNode;
    image?: string;
    animations?: boolean;
    children?: React.ReactNode;
    reference?: React.RefObject<HTMLDivElement>;
};

const accordion = {
    open: {
        opacity: 1,
        height: 'auto',
        marginTop: '15px',
    },
    closed: {
        opacity: 0,
        height: 0,
        marginTop: '0px',
    },
};

export default function NavigationBox({
    displayMode,
    top,
    right,
    left,
    x,
    y,
    title,
    text,
    subtitle,
    exercise,
    next,
    previous,
    audio,
    finishedButtons,
    ProgressBar,
    image,
    animations,
    children,
    reference,
}: Props) {
    const [textExpanded, setTextExpanded] = useState(!audio);

    const [fadingTitle, setFadingTitle] = useState(true);
    const [localTitle, setLocalTitle] = useState(title);
    const [shouldAnimate, setShouldAnimate] = useState(false);

    useEffect(() => {
        setFadingTitle(true); // fade out
        setTimeout(() => {
            setFadingTitle(false); // fade back in
            setLocalTitle(title);
        }, 200); // animation timing offset
    }, [title]);

    const [fadingText, setFadingText] = useState(true);
    const [localText, setLocalText] = useState(text);

    useEffect(() => {
        setFadingText(true); // fade out
        setTimeout(() => {
            setFadingText(false); // fade back in
            setLocalText(text);
        }, 200); // animation timing offset
    }, [text]);

    const framerValues = {
        top: displayMode === 'centered' ? '50%' : top,
        right,
        bottom: '',
        left,
        x,
        y,
    };

    return (
        <motion.div
            styleName={`container ${displayMode}`}
            animate={framerValues}
            initial={framerValues}
            ref={reference}
        >
            <div styleName="wrapper">
                <div styleName="top">
                    <div styleName="left">
                        {localTitle && (
                            <div styleName="title-wrapper">
                                <h3 style={{ opacity: fadingTitle ? 0 : 1 }}>
                                    {localTitle}
                                </h3>
                            </div>
                        )}
                        {subtitle && (
                            <motion.p>
                                <b>{subtitle}</b>
                                {exercise}
                            </motion.p>
                        )}
                    </div>
                    <div styleName="right">
                        <div styleName="round-buttons">
                            <Expand
                                textExpended={textExpanded}
                                setTextExpended={setTextExpanded}
                                text={localText}
                            />
                            {!!audio && (
                                <Speaker
                                    audio={audio}
                                    setShouldAnimate={setShouldAnimate}
                                    animations={animations}
                                />
                            )}
                        </div>
                        {(next || previous) && (
                            <Navigate
                                next={next}
                                previous={previous}
                                shouldAnimate={shouldAnimate}
                            />
                        )}
                    </div>
                </div>
                <AnimatePresence>
                    {textExpanded && text && (
                        <motion.div
                            variants={accordion}
                            initial="closed"
                            exit="closed"
                            animate={textExpanded ? 'open' : 'closed'}
                            styleName="text-wrapper"
                        >
                            {typeof localText === 'string' ? (
                                <p
                                    style={{ opacity: fadingText ? 0 : 1 }}
                                    dangerouslySetInnerHTML={
                                        localText
                                            ? { __html: localText }
                                            : undefined
                                    }
                                />
                            ) : (
                                localText
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
                {!!children && children}
                {image ? (
                    <div styleName="image">
                        <img src={image} alt="tile" />
                    </div>
                ) : (
                    <></>
                )}

                {finishedButtons === 'homework' && <HomeworkFinishedButtons />}
                {finishedButtons === 'audio-exercise' && (
                    <AudioFinishedButtons />
                )}
                {finishedButtons === 'flash-exercise' && (
                    <FlashFinishedButtons />
                )}
            </div>
            {ProgressBar && ProgressBar}
        </motion.div>
    );
}
